import { useState, useRef } from "react";
import heroBg from "./images/topbacground.jpg";
import Hinicio_description from "./images/Hinicio_description.png";
import Covalent_description from "./images/Covalent_description.png";
import Hinicio_footer from "./images/Hinicio_footer.png";
import Covalent_footer from "./images/Covalent_footer.png";
import contact_image from "./images/contact_image.jpg";



const T = {
  bg:           "#f8fafc",
  bgCard:       "#ffffff",
  bgMuted:      "#f1f5f9",
  border:       "#e2e8f0",
  borderStrong: "#cbd5e1",
  text:         "#0f172a",
  textSub:      "#475569",
  textMuted:    "#94a3b8",
  teal:         "#0d9488",
  tealLight:    "#ccfbf1",
  tealDark:     "#0f766e",
  tealHero:     "#0d4f4a",
  amber:        "#d97706",
  amberLight:   "#fef3c7",
  navy:         "#0f172a",
  monoFont:     "'IBM Plex Mono', monospace",
  bodyFont:     "'IBM Plex Sans', sans-serif",
  displayFont:  "'Bebas Neue', sans-serif",
};

/* ─── Inline markdown renderer ─────────────────────────────────── */
function renderMd(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^---+$/.test(line.trim())) { out.push(<hr key={i} style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "20px 0" }} />); i++; continue; }
    if (line.startsWith("# ")) { out.push(<h1 key={i} style={{ fontFamily: T.displayFont, fontSize: 24, letterSpacing: 1, color: T.teal, marginBottom: 10 }}>{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith("## ")) { out.push(<h2 key={i} style={{ fontFamily: T.monoFont, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: T.teal, marginTop: 24, marginBottom: 10, borderBottom: `1px solid ${T.border}`, paddingBottom: 6 }}>{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith("### ")) { out.push(<h3 key={i} style={{ fontFamily: T.monoFont, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: T.textSub, marginTop: 16, marginBottom: 6 }}>{line.slice(4)}</h3>); i++; continue; }
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) { items.push(<li key={i} style={{ marginBottom: 5, lineHeight: 1.65, color: T.textSub }}>{inlineFmt(lines[i].slice(2))}</li>); i++; }
      out.push(<ul key={`ul-${i}`} style={{ paddingLeft: 20, marginBottom: 10 }}>{items}</ul>); continue;
    }
    if (line.trim() === "") { out.push(<div key={i} style={{ height: 6 }} />); i++; continue; }
    out.push(<p key={i} style={{ marginBottom: 8, lineHeight: 1.75, color: T.textSub }}>{inlineFmt(line)}</p>);
    i++;
  }
  return out;
}

function inlineFmt(text) {
  const parts = []; const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g; let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[0].startsWith("**")) parts.push(<strong key={key++} style={{ color: T.text, fontWeight: 600 }}>{m[2]}</strong>);
    else if (m[0].startsWith("*")) parts.push(<em key={key++} style={{ color: T.teal, fontStyle: "italic" }}>{m[3]}</em>);
    else parts.push(<code key={key++} style={{ fontFamily: T.monoFont, fontSize: "0.88em", background: T.tealLight, padding: "1px 5px", borderRadius: 3, color: T.tealDark }}>{m[4]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const DOC_CONTENT = `# Fleet Decarbonisation Planning Tool — What This App Does and Does Not Do

---

## Purpose

This tool is a **strategic planning aid** for evaluating how a fleet of container vessels could reduce its greenhouse gas emissions and manage fuel costs across the 2025–2050 regulatory timeline. It is designed to answer one core question: *if we operate these voyages with these vessels and progressively shift our fuel mix, what are the cost and emissions implications, and are we compliant with FuelEU Maritime regulations?*

It is **not** an operational tool. It does not replace a voyage management system, a fuel management system, or detailed ship engineering software. Every number it produces should be treated as a planning-level estimate, not an operational instruction.

---

## What the App Does

### Route Building
- The user assembles voyages by chaining together legs between a fixed set of 23 major global ports (Shanghai, Rotterdam, Singapore, Long Beach, and so on).
- A voyage can be as simple as a single leg between two ports, or it can chain up to six ports in sequence. There is no requirement for the voyage to be circular or to return to the starting port.
- Each leg between two ports has a **fixed, pre-calculated distance** (nautical miles) and a **fixed path** (a set of pre-defined maritime coordinates). These paths respect oceanic routing — no land crossing — and include key passages such as the Suez Canal and Strait of Malacca where applicable.
- Because this tool is focused on FuelEU Maritime compliance, the ports defined in a voyage should reflect **ports of call** in the regulatory sense — that is, ports where cargo is loaded or discharged, or where passengers embark or disembark. Intermediate stops for bunkering, crew changes, or other technical purposes that do not constitute a port of call under the regulation should not be added as voyage legs. Including non-port-of-call stops would distort the distance, the EU coverage classification, and therefore the compliance calculation.
- Each saved voyage can be assigned a **number of trips per year**, reflecting how often that route is operated annually.

### Vessel Parameters
- Each voyage is assigned a vessel profile, either from four preset archetypes (Feeder 1k TEU, Panamax 5k TEU, Post-Panamax 8k TEU, ULCV 20k TEU) or a fully custom configuration.
- The editable vessel parameters are: vessel type, capacity (TEU), speed (knots), baseline fuel type, and fuel consumption rate (tonnes per day).
- These parameters are applied uniformly to the entire voyage and every trip of that voyage. There is no variation by leg, season, or load condition.

### Fuel Strategy
- Each voyage has a **fuel strategy timeline** covering six scenario years: 2025, 2030, 2035, 2040, 2045, and 2050.
- For each year, the user can define a **base fuel** and up to **two blended fuels** with their respective energy share percentages.
- Blends are defined as a percentage of total voyage energy (in MJ), not a percentage of physical tonnes. The tool converts energy shares to actual tonnes burned per fuel based on each fuel's lower heating value (LHV).
- Years can be individually activated or deactivated per voyage, to model routes being added or retired over time.
- The tool includes an **optimiser** that automatically calculates the minimum blend share needed across the fleet to meet the FuelEU Maritime GHG intensity target for each year, prioritising the cheapest blend fuel first.

### Fuel Library
- The tool ships with a set of conventional baseline fuels (HFO, VLSFO, MGO, LNG, Methanol) and a large library of advanced and alternative fuels (biofuels, e-fuels, ammonia, hydrogen, and blends thereof).
- Each fuel has editable parameters: cost (base, low, and high scenarios), CO₂ factor (tCO₂/t fuel), HFO equivalent factor (tHFOeq/t fuel), and LHV (MJ/kg).
- The user can define fully custom fuels with any combination of these parameters.
- Activating a fuel from the library makes it available for selection in the fuel strategy timeline.

### Calculations
- **Fuel consumption** is calculated from distance, speed, and consumption rate: tonnes burned = (distance ÷ (speed × 24)) × consumption rate (t/day).
- **HFO equivalent** is physical tonnes × the fuel's HFO equivalent factor, used as the energy normalisation basis for regulatory calculations.
- **CO₂ emissions** are physical tonnes × the fuel's CO₂ emission factor.
- **Fuel cost** is physical tonnes × cost per tonne, with low and high scenario bounds where available.
- **FuelEU Maritime GHG intensity** is calculated as eligible CO₂ (tCO₂) ÷ eligible energy (MJ) × 1,000,000, expressed in gCO₂/MJ. Eligibility is weighted by EU coverage factors: 100% for inter-European voyages, 50% for voyages with one EU port, and 0% for voyages fully outside the EU.
- **RFNBO double-counting credit**: when a fuel is flagged as an RFNBO (Renewable Fuel of Non-Biological Origin), the tool applies the FuelEU Maritime double-counting rule (Art. 25, Reg. EU 2023/1805) — the physical energy of that fuel is credited twice in the GHG intensity denominator. This lowers attained GHG intensity without changing physical CO₂ or tonnes burned. The flag is off by default, must be enabled per fuel, and physical consumption tables always show real quantities with the credit broken out separately.
- **FuelEU non-compliance penalty** follows Article 23 of Regulation 2023/1805: €2,400 per tonne of VLSFO equivalent of the GHG intensity shortfall, with a +10% escalation per consecutive non-compliant year. All penalty parameters are editable.
- **Abatement cost** is calculated as the incremental fuel cost of a scenario versus the baseline, divided by the CO₂ avoided, expressed in USD per tonne of CO₂.

### Data Tables and Analytics
- The **Data Tables** tab shows per-voyage summary (distance, travel time, fuel consumed, and HFO equivalent per single trip), fleet-level fuel consumption broken down by EU coverage region, HFO equivalent totals, CO₂ emissions baseline vs scenario, energy and GHG intensity, fuel cost with low/base/high scenarios, and the full FuelEU penalty escalation schedule across all scenario years.
- The **Analytics** tab shows charts for CO₂ intensity trajectory vs the FuelEU target line, scenario fuel cost stacked by fuel type, cost scenario ranges with uncertainty bands, and fuel cost share evolution over time.
- All calculations update automatically whenever a voyage, vessel parameter, fuel parameter, or scenario setting is changed.

### Import / Export
- The full fleet configuration — voyages, vessel parameters, fuel strategy timelines, active fuel library, custom fuels, and regulatory parameters — can be exported to a JSON file and re-imported at any time.
- The export format is versioned (currently v3). Older exports are normalised on import so earlier sessions remain loadable.

### Saved Cases and Presets
- Work can be saved as a **named case** directly in the browser (via the 💾 Save Case button in the footer). Cases are stored in the browser's local storage and persist between sessions on the same device and browser.
- Multiple cases can be saved under different names. On re-opening the tool, the **startup modal** lets the user choose to start fresh, load a saved case, or load a built-in **fleet preset**.
- Presets are pre-configured fleet scenarios (defined in \`presets.js\`) that demonstrate typical use cases — short-sea feeder loops, deep-sea container services, tanker routes, and so on. They provide a a fast starting point for exploring the tool without manual route building.
- Saving a case overwrites or creates a new entry in local storage; it does not export a file. For sharing or backup, use the JSON export function.

### Fuel Allocation
- The **Allocation** tab visualises how FuelEU Maritime's in-scope energy is constructed from the fleet's voyages, and lets the user toggle between **proportional** and **optimal** allocation modes.
- **Proportional mode** (default): each voyage contributes its actual fuel blend pro-rata into the in-scope window. This is the conservative, naïve approach.
- **Optimal mode**: takes advantage of the regulation's annual fleet-level allocation rule. Under Reg. EU 2023/1805, the company chooses *which* 50% of a mixed voyage's energy (one EU port) enters the compliance calculation. The optimal algorithm fills that 50% window with the fleet's cleanest fuels first, potentially reducing the attained GHG intensity significantly.
- The toggle in the Allocation tab is a **global switch** — all penalty calculations, the GHG intensity chart, cost charts, and the Data Tables penalty schedule all reflect the selected mode.
- The two-strip visualisation shows the raw energy consumed (top bar) and the in-scope allocation (bottom bar), with hover-activated connectors tracing each fuel segment between the two strips.
- **Regulation note**: allocation only applies to mixed voyages (one EU port, 50% scope). Intra-EU voyages (both ports EU) always count 100% in scope — no allocation choice exists for those legs.

---

## What the App Does Not Do

### Routes Are Fixed and Cannot Be Customised
The connections between ports are pre-defined. **The user cannot draw a new route, modify an existing path, add waypoints, or change the distance of a leg.** If a specific real-world routing (for example, avoiding a particular strait, or calling at a port not in the list of 23) is needed, it cannot be represented. The tool covers a fixed network of the most commercially significant global port pairs.

### No Real Vessel Data
Vessel parameters are typed in manually. The tool has no connection to any vessel database, AIS feed, or technical management system. Speed and consumption values are single fixed numbers — there is no speed-consumption curve, no weather adjustment, no ballast vs laden distinction, and no port maneuvering or auxiliary consumption. A vessel consuming 55 t/day is assumed to consume exactly 55 t/day on every leg, every trip, regardless of conditions.

### No Fuel Tank or Bunkering Feasibility Check
The tool does not know the fuel tank capacity of any vessel. It calculates how many tonnes of fuel a voyage requires, but it **does not check whether the vessel can carry that amount**. A very long voyage may require more fuel than a real vessel of that class could physically bunker. This must be verified separately.

Similarly, the tool does not model bunkering stops, intermediate refuelling, or the availability of specific fuels at specific ports. It assumes every fuel defined in a scenario is available wherever and whenever the vessel needs it.

### Blended Fuels Are Not Checked for Technical Feasibility
When a user defines a blend of, say, 30% bio-methanol and 70% VLSFO, the tool treats this purely as an energy-weighted cost and emissions calculation. It does **not** assess whether the vessel's engines are technically capable of burning that blend, whether the blend is physically stable, whether dedicated tankage or equipment modifications are required, or whether the blend meets any specific fuel standard. The feasibility of any blended fuel strategy must be evaluated by naval architects and engine manufacturers independently.

### No Port Stay, Port Costs, or Cargo Economics
The tool calculates travel time only. Port stays (even though the route builder allows the user to assign days at port for scheduling purposes) do not affect any cost or fuel calculation — they exist purely as schedule information. Port dues, canal transit fees, cargo handling costs, freight rates, and vessel operating expenses beyond fuel are entirely outside scope. The cost output is **fuel cost only**.

### No Cargo Load or Utilisation
Capacity (TEU) is recorded as a vessel parameter but plays no role in any calculation. The tool does not model load factor, cargo weight, or how utilisation affects fuel consumption. It is assumed the vessel always operates at its stated consumption rate.

### No Engine Type or Dual-Fuel Logic
The tool does not distinguish between two-stroke and four-stroke engines, diesel-cycle and Otto-cycle engines, or single-fuel and dual-fuel configurations. Selecting "LNG" as a base fuel does not imply that the vessel has an LNG-capable engine — it is simply a fuel with different cost, CO₂, and LHV parameters. Whether a vessel can actually burn a given fuel is not checked.

### Scenario Years Are Snapshots, Not Continuous Simulations
The tool models six discrete years (2025, 2030, 2035, 2040, 2045, and 2050). It does not interpolate between years, model year-by-year transitions, account for fleet renewal cycles, or simulate the time and cost of retrofitting vessels. Each scenario year is an independent steady-state calculation: "if the fleet operated all year like this, here is the outcome." **Every cost figure shown — in tables, stat cards, and charts — is an annual (per calendar year) value.** A cost of, say, 200 MUSD labeled "2030" means the fleet would spend 200 MUSD in 2030, and again in 2031, 2032, 2033, and 2034 if conditions held steady — it is not the combined total for the whole 2030–2034 block.

### No Uncertainty in Distances or Travel Times
Travel time is calculated from fixed distance and a single fixed speed. Weather, port congestion, canal delays, currents, and seasonal routing deviations are not modelled. Actual voyage durations will always differ from the tool's estimates.

### CO₂ Factors Are Tank-to-Wake by Default
The emissions calculation uses tank-to-wake CO₂ factors (combustion emissions only) unless the user manually enters a well-to-wake or lifecycle factor for a given fuel. The tool does not automatically apply well-to-wake factors from any specific regulatory dataset. Users who need lifecycle emissions must manually input the appropriate CO₂ factor for each fuel. This is particularly important for biofuels and e-fuels, where the well-to-wake and tank-to-wake figures can differ dramatically.

### FuelEU Calculations Are an Approximation
The FuelEU Maritime compliance calculation implemented here follows the published regulation logic, but it is a simplified model. It does not account for the full scope of the regulation's definitions of "energy used on board," does not model shore power, waste heat recovery, or wind-assist contributions, and does not replicate the exact verification methodology that flag state authorities will apply. Compliance status shown in this tool should not be treated as a formal regulatory determination.

### No Multi-Vessel Fleet per Voyage
Each saved voyage is assigned a single vessel. The tool cannot model a scenario where multiple different vessels operate the same route, or where a vessel serves multiple routes simultaneously.

### Saved Cases Are Browser-Local and Not Backed Up
The tool now supports saving named cases to the browser's local storage, which persist between sessions on the same device and browser. However, local storage is **not a reliable long-term archive**: it can be cleared by the user, the browser, or OS-level cleanup. There is no server, no user account, and no synchronisation between devices or browsers. For reliable backup and sharing, always use the JSON export function. If a session is closed without saving or exporting, work performed since the last save is lost.

---

## Summary

This tool is best used as a **what-if calculator** for fleet decarbonisation strategy. It is well suited to answering questions like: *What blend share of bio-LNG would we need across our fleet to be FuelEU compliant in 2035? What is the cost delta compared to staying on VLSFO? Which voyages drive the most emissions exposure? How much does optimal fuel allocation improve our compliance position on mixed voyages?* It is not suited to answering questions about whether a specific voyage is operationally executable, whether a vessel needs a retrofit, or whether a bunkering plan is physically possible. For those questions, the outputs of this tool should be taken as inputs to more detailed engineering and operational analysis.`;


function DocModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15,23,42,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "32px 20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          width: "100%", maxWidth: 800,
          maxHeight: "84vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 28px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bgMuted,
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: T.monoFont, fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", color: T.teal, marginBottom: 2 }}>Documentation</div>
            <div style={{ fontFamily: T.displayFont, fontSize: 19, letterSpacing: 1, color: T.text }}>Tool Scope &amp; Limitations</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: `1px solid ${T.border}`,
              borderRadius: 6, color: T.textMuted, fontFamily: T.monoFont,
              fontSize: 16, width: 34, height: 34, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal; e.currentTarget.style.color = T.teal; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
          >×</button>
        </div>
        {/* Body */}
        <div style={{ overflowY: "auto", padding: "24px 28px", fontSize: 13, lineHeight: 1.7, fontFamily: T.bodyFont }}>
          {renderMd(DOC_CONTENT)}
        </div>
      </div>
    </div>
  );
}

/* ─── Hinicio SVG logo ───────────────────────────────────────────── */
function HinicioLogo({ height = 40 }) {
  return (
    <img
          src={Hinicio_description}
          alt=""
          aria-hidden="true"
          style={{
            position: "relative", inset: 0,
            width: "100%", height: "100%",
            objectFit: "contain",
            objectPosition: "left center",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
  );
}

/* ─── Covalent IQ SVG logo ───────────────────────────────────────── */
function CovalentLogo({ height = 36, light = false }) {
  return (
    <img
          src={Covalent_description}
          alt=""
          aria-hidden="true"
          style={{
            position: "relative", inset: 0,
            width: "100%", height: "40%",
            objectFit: "contain",
            objectPosition: "center center",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
  );
}

/* ─── Contact form ───────────────────────────────────────────────── */
export function ContactForm({ onSent, onContact }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const REQUIRED_FIELDS = ["name", "email", "company"];
  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "10px 12px",
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    fontFamily: T.bodyFont, fontSize: 13,
    color: T.text, background: T.bgMuted,
    outline: "none",
    marginTop: 4,
  };

  async function handleSend() {
    const missing = REQUIRED_FIELDS.filter(key => !form[key].trim());
    if (missing.length > 0) {
      setError("Please fill in name, email, and company.");
      return;
    }
    setError("");
    setSending(true);
    const success = await onContact?.(form);
    setSending(false);
    if (success) {
      onSent();
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {[
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "company", label: "Company" },
        { key: "role", label: "Role" },
      ].map(({ key, label }) => {
        const isRequired = REQUIRED_FIELDS.includes(key);
        const isMissing = error && isRequired && !form[key].trim();
        return (
          <div key={key}>
            <label style={{ fontFamily: T.bodyFont, fontSize: 13, color: T.textSub }}>
              {label}{isRequired && <span style={{ color: "#ef4444" }}> *</span>}
            </label>
            <input
              style={{
                ...inputStyle,
                borderColor: isMissing ? "#ef4444" : T.border,
              }}
              type={key === "email" ? "email" : "text"}
              value={form[key]}
              onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
            />
          </div>
        );
      })}
      {error && (
        <div style={{ fontFamily: T.bodyFont, fontSize: 12, color: "#ef4444", marginTop: -6 }}>
          {error}
        </div>
      )}
      <button
        onClick={handleSend}
        disabled={sending}
        style={{
          background: sending ? "#94a3b8" : T.teal, color: "#fff", border: "none",
          borderRadius: 6, padding: "12px 0",
          fontFamily: T.monoFont, fontSize: 11,
          letterSpacing: "2px", textTransform: "uppercase",
          cursor: sending ? "not-allowed" : "pointer", marginTop: 4,
          transition: "background 0.15s",
        }}
        onMouseEnter={e => { if (!sending) e.currentTarget.style.background = T.tealDark; }}
        onMouseLeave={e => { if (!sending) e.currentTarget.style.background = T.teal; }}
      >{sending ? "Sending…" : "Send"}</button>
    </div>
  );
}

/* ─── Contact section with height-locked right column ───────────── */
function ContactSection({ CONTACT_IMG_POSITION, onContact }) {
  const [sent, setSent] = useState(false);
  const rightColRef = useRef(null);

  function handleSend() {
    // Lock the right column to its current pixel height before collapsing
    if (rightColRef.current) {
      rightColRef.current.style.height = rightColRef.current.offsetHeight + "px";
      rightColRef.current.style.minHeight = "unset";
    }
    setSent(true);
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "stretch" }}>
      {/* Left — contact image, stretches to match right column height */}
      <div style={{ borderRadius: 10, overflow: "hidden", minHeight: 200 }}>
        <img
          src={contact_image}
          alt=""
          style={{
            display: "block",
            width: "100%", height: "100%",
            objectFit: "cover",
            objectPosition: `${CONTACT_IMG_POSITION} center`,
            borderRadius: 10,
          }}
        />
      </div>

      {/* Right — CTA text above form */}
      <div ref={rightColRef} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ fontFamily: T.displayFont, fontSize: 16, letterSpacing: 3, color: T.teal }}>
                Take your analysis to the next level
              </div>
        <p style={{ fontFamily: T.bodyFont, fontSize: 14, color: T.textSub, lineHeight: 1.75, margin: 0, flexShrink: 0 }}>
          Leave your details and an expert from Hinicio or CovalentIQ will reach out to explore how to optimise your FuelEU compliance strategy and reduce your decarbonisation costs.
        </p>
        <div style={{
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          padding: "28px 28px",
          boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
          display: "flex", flexDirection: "column", flex: 1,
        }}>
          {sent ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontFamily: T.monoFont, fontSize: 13, color: T.teal, letterSpacing: 1, textAlign: "center" }}>
                ✓ Message sent — we'll be in touch soon.
              </div>
            </div>
          ) : (
            <ContactForm onSent={handleSend} onContact={onContact} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main landing page ─────────────────────────────────────────── */
export default function LandingPage({ onEnter, onContact, onshowDocumentation }) {
  const [showDoc, setShowDoc] = useState(false);

  function handleShowDocumentation() {
    onshowDocumentation();
    setShowDoc(true);
  }

  

  const companies = [
    {
      logo: <HinicioLogo/>,
      name: "HINICIO",
      color: T.teal,
      body1: "Founded in 2006, Hinicio is a leading consulting firm specialized in the decarbonization challenges of hard-to-abate sectors, with 20 years of experience in hydrogen and its derivatives. With a portfolio of over 1,000 projects across 50 countries, we advise governments, multilateral development banks, investors, banks, and project developers.",
      body2: "Our services include the formulation of corporate and national strategies, the development of public policies, advisory support for investment decisions, and the execution of studies and projects with high technological and economic complexity. Unified by purpose and driven by expertise, we deliver solutions across integrated energy systems, transport and mobility, and heavy and process industries.",
      body3: "Hinicio leads the transition to sustainable transport by delivering expert consulting services to implement innovative, low- and zero-emission solutions across all modes of transport, whether through direct electrification or the deployment of renewable molecules.",
      url: "https://hinicio.com/",
    },
    {
      logo: <CovalentLogo/>,
      name: "COVALENTIQ",
      color: T.teal,
      body1: "CovalentIQ is a specialized consulting firm focused on strategic insight for the chemicals value chain through bespoke primary research and analytics. Combining deep sector expertise with transparent research processes, we help corporate and private equity clients address complex questions across markets, customers, products, transactions, JVs and alliances, and decarbonization.",
      body2: "Through curated expert interview and B2B survey programs, as well as deep analytical modeling expertise, we uncover insights that are not available in syndicated reports or public data. We then translate those insights into clear, decision-grade analytics that help clients understand market dynamics, validate assumptions, quantify opportunities, and make better strategic decisions.",
      body3: "CovalentIQ has also authored and published the Levelized Cost of Fuels (LCOF) model series, a techno-economic analysis covering 70 e-fuel and biofuel pathways for maritime shipping.",
      url: "https://covalent-iq.com",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#d1d8e0", color: T.text, fontFamily: T.bodyFont, display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* ── 1595px centred column — gutters show the root background colour ── */}
      <div style={{ width: "100%", maxWidth: 1595, background: T.bg, display: "flex", flexDirection: "column", minHeight: "100vh", flexShrink: 0 }}>

      {/* ── STICKY NAV ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 48px",
        background: "rgba(255,255,255,0.97)",
        borderBottom: `1px solid ${T.border}`,
        boxShadow: "0 1px 6px rgba(15,23,42,0.07)",
        flexShrink: 0,
      }}>
        {/* Left: tool logo + divider + company logos */}
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {/* Tool logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
              <path d="M4 20l2.5-9h17l2.5 9H4z" stroke={T.teal} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
              <path d="M11 11V8a1 1 0 011-1h7a1 1 0 011 1v3" stroke={T.teal} strokeWidth="1.6" fill="none"/>
              <path d="M2 23c2.5 2 6 2 8.5 0s6-2 8.5 0 4.5 0 7-2" stroke={T.teal} strokeWidth="1.6" strokeLinecap="round" fill="none"/>
            </svg>
            <div>
              <div style={{ fontFamily: T.displayFont, fontSize: 18, letterSpacing: 3, color: T.navy, lineHeight: 1 }}>FLEET DECARB</div>
              <div style={{ fontFamily: T.monoFont, fontSize: 8, letterSpacing: "2px", color: T.textMuted, textTransform: "uppercase", marginTop: 1 }}>FuelEU Maritime · Planning Tool</div>
            </div>
          </div>
          <div style={{ width: 1, height: 24, background: T.border }} />
        </div>

        {/* Right: nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => handleShowDocumentation()}
            style={{
              background: "none", border: `1px solid ${T.border}`,
              borderRadius: 6, color: T.textSub,
              fontFamily: T.monoFont, fontSize: 9,
              letterSpacing: "1.5px", textTransform: "uppercase",
              padding: "7px 14px", cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal; e.currentTarget.style.color = T.teal; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}
          >⊙ Documentation</button>
          <button
            onClick={onEnter}
            style={{
              background: T.teal, border: "none",
              borderRadius: 6, color: "#fff",
              fontFamily: T.monoFont, fontSize: 9,
              letterSpacing: "1.5px", textTransform: "uppercase",
              padding: "7px 18px", cursor: "pointer", transition: "background 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.tealDark; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.teal; }}
          >Open Tool →</button>
        </div>
      </header>

      {/* ── HERO ── */}
      {/*
        The image renders at its natural size (1595×725 px), centred horizontally.
        On screens wider than 1595px the page background fills the gutters.
        On narrower screens the image scales down proportionally (maxWidth: "100%").

        ┌─ CONTENT BOX POSITION CONTROLS ──────────────────────────────────┐
        │ Adjust these four values to reposition the text box over the     │
        │ background image. All values are % of the image dimensions.      │
        │                                                                   │
        │  HERO_BOX_LEFT    – distance from left edge of image  (% width)  │
        │  HERO_BOX_TOP     – distance from top edge of image   (% height) │
        │  HERO_BOX_BOTTOM  – distance from bottom edge         (% height) │
        │  HERO_BOX_WIDTH   – width of the box                  (% width)  │
        └───────────────────────────────────────────────────────────────────┘
      */}
      {(() => {
        const HERO_BOX_LEFT   = 5;   // % from left edge of image
        const HERO_BOX_TOP    = 30;   // % from top of image
        const HERO_BOX_BOTTOM = 10;   // % from bottom of image
        const HERO_BOX_WIDTH  = 40;   // % of image width

        return (
          /* Outer: full-width row that centres the 1595px image and shows gutters */
          <div style={{
            display: "flex",
            justifyContent: "center",
            background: T.bg,
            flexShrink: 0,
          }}>
            {/* Inner: capped at image natural width, image drives the height */}
            <div style={{
              position: "relative",
              width: 1595,
              maxWidth: "100%",
              flexShrink: 0,
            }}>
              {/* ── Background photo — natural size, no cropping ── */}
              <img
                src={heroBg}
                alt=""
                aria-hidden="true"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />

              {/* ── Floating content box, positioned by the % constants above ── */}
              <div style={{
                position: "absolute",
                left:   `${HERO_BOX_LEFT}%`,
                top:    `${HERO_BOX_TOP}%`,
                bottom: `${HERO_BOX_BOTTOM}%`,
                width:  `${HERO_BOX_WIDTH}%`,
                background: "rgba(10, 40, 38, 0.82)",
                backdropFilter: "blur(2px)",
                borderRadius: 8,
                display: "flex", flexDirection: "column", justifyContent: "center",
                padding: "32px 36px",
                boxSizing: "border-box",
                overflow: "hidden",
              }}>
                <div style={{ fontFamily: T.monoFont, fontSize: 9, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 14 }}>
                  Strategic Fleet Compliance Modelling · 2025 – 2050
                </div>

                <h1 style={{
                  fontFamily: T.displayFont,
                  fontSize: "clamp(24px, 2.8vw, 52px)",
                  letterSpacing: 4, color: "#ffffff",
                  lineHeight: 1.05, margin: "0 0 16px",
                }}>
                  FLEET DECARBONISATION<br/>PLANNING TOOL
                </h1>

                <p style={{
                  fontFamily: T.bodyFont, fontSize: 13, color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.7, margin: "0 0 24px",
                }}>
                  Model fuel strategy, FuelEU Maritime compliance, and cost trajectories across your fleet. Start from a preset, pick up a saved case, or build from scratch — then explore optimal fuel allocation to see the real compliance opportunity.
                </p>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <button
                    onClick={onEnter}
                    style={{
                      background: T.teal, border: `2px solid ${T.teal}`,
                      borderRadius: 6, color: "#fff",
                      fontFamily: T.monoFont, fontSize: 10,
                      letterSpacing: "2px", textTransform: "uppercase",
                      padding: "11px 24px", cursor: "pointer", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.tealDark; e.currentTarget.style.borderColor = T.tealDark; }}
                    onMouseLeave={e => { e.currentTarget.style.background = T.teal; e.currentTarget.style.borderColor = T.teal; }}
                  >Enter Tool</button>
                  <button
                    onClick={() => setShowDoc(true)}
                    style={{
                      background: "transparent", border: "2px solid rgba(255,255,255,0.4)",
                      borderRadius: 6, color: "rgba(255,255,255,0.85)",
                      fontFamily: T.monoFont, fontSize: 10,
                      letterSpacing: "2px", textTransform: "uppercase",
                      padding: "11px 20px", cursor: "pointer", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
                  >Scope & Limitations</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── FEATURE STRIP ── */}
      <div style={{
        background: T.bgCard,
        borderBottom: `1px solid ${T.border}`,
        padding: "0",
        display: "flex", alignItems: "stretch",
        flexShrink: 0, overflowX: "auto",
      }}>
        {["Fuel Strategy Timeline", "FuelEU Maritime Compliance", "Optimal Fuel Allocation", "Blend Optimiser"].map((f, i, arr) => (
          <div key={f} style={{
            flex: "1 1 0",
            minWidth: 140,
            padding: "20px 24px",
            textAlign: "center",
            borderRight: i < arr.length - 1 ? `1px solid ${T.border}` : "none",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontFamily: T.bodyFont, fontSize: 13, color: T.textSub, fontWeight: 400 }}>{f}</span>
          </div>
        ))}
      </div>

      {/* ── COLLABORATION SECTION ── */}
      <div style={{ padding: "64px 48px", maxWidth: 1000, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        {/* Section heading */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ fontFamily: T.displayFont, fontSize: 28, letterSpacing: 4, color: T.teal }}>
            A COLLABORATION BETWEEN
          </div>
          <div style={{ width: 48, height: 3, background: T.teal, margin: "12px auto 0" }} />
        </div>

        {/* Two cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
          {companies.map((co, idx) => (
            <div key={idx} style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: "32px 28px 28px",
              display: "flex", flexDirection: "column", gap: 20,
              boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
              transition: "box-shadow 0.2s, border-color 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(15,23,42,0.1)"; e.currentTarget.style.borderColor = T.teal + "55"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(15,23,42,0.06)"; e.currentTarget.style.borderColor = T.border; }}
            >
              {/* Logo area — fixed height so horizontal dividers align across both cards */}
              <div style={{ height: 90, display: "flex", alignItems: "center" }}>
                {co.logo}
              </div>

              <div style={{ height: 1, background: T.border }} />

              {/* Company name */}
              <div style={{ fontFamily: T.displayFont, fontSize: 18, letterSpacing: 3, color: co.color }}>
                {co.name}
              </div>

              {/* Body */}
              <p style={{ fontFamily: T.bodyFont, fontSize: 13, color: T.textSub, lineHeight: 1.75, margin: 0 }}>
                {co.body1}
              </p>
              <p style={{ fontFamily: T.bodyFont, fontSize: 13, color: T.textSub, lineHeight: 1.75, margin: 0 }}>
                {co.body2}
              </p>
              <p style={{ fontFamily: T.bodyFont, fontSize: 13, color: T.textSub, lineHeight: 1.75, margin: 0 }}>
                {co.body3}
              </p>

              {/* Read More button */}
              <div style={{ marginTop: "auto" }}>
                <a
                  href={co.url}
                  style={{
                    display: "inline-block",
                    background: T.teal,
                    color: "#fff",
                    fontFamily: T.bodyFont, fontSize: 13,
                    padding: "10px 28px",
                    borderRadius: 6,
                    textDecoration: "none",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.tealDark; }}
                  onMouseLeave={e => { e.currentTarget.style.background = T.teal; }}
                >Read More</a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CONTACT SECTION ── */}
      <div style={{ padding: "64px 48px", maxWidth: 1000, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        {/* Section heading */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ fontFamily: T.displayFont, fontSize: 28, letterSpacing: 4, color: T.teal }}>
            CONTACT US
          </div>
          <div style={{ width: 48, height: 3, background: T.teal, margin: "12px auto 0" }} />
        </div>

        {/* Two-column: image left, CTA+form right */}
        {/*
          ── CONTACT IMAGE CROP CONTROL ─────────────────────────────────────
          CONTACT_IMG_POSITION controls which part of the image is visible.
          It maps to CSS object-position (horizontal crop):
            "left"   → show left side of image
            "center" → show centre  (default)
            "right"  → show right side
            "25%"    → show 25% from the left, etc.
          ──────────────────────────────────────────────────────────────────── */}
        {(() => {
          const CONTACT_IMG_POSITION = "20%"; // ← adjust to crop horizontally

          return <ContactSection CONTACT_IMG_POSITION={CONTACT_IMG_POSITION} onContact={onContact} />;
        })()}
      </div>

      {/* ── FOOTER ── */}
      <footer style={{
        marginTop: "auto",
        background: T.tealHero,
        padding: "32px 48px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 24,
        flexShrink: 0,
      }}>
        {/* Left */}
        <div>
          <div style={{ fontFamily: T.monoFont, fontSize: 11, color: "rgba(255,255,255,0.9)", fontWeight: 600, marginBottom: 6 }}>
            Contact us
          </div>
          <div style={{ fontFamily: T.bodyFont, fontSize: 12, color: "rgba(255,255,255,0.6)", lineHeight: 1.8 }}>
            Info@hinicio.com<br/>covalent-iq.com
          </div>
        </div>

        {/* Right — logos */}
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          <img
            src={Hinicio_footer}
            alt=""
            aria-hidden="true"
            style={{
              position: "relative", width: "150px"
            }}
          />
          <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.2)" }} />
          <img
            src={Covalent_footer}
            alt=""
            aria-hidden="true"
            style={{
              position: "relative",  width: "150px"
            }}
          />
        </div>
      </footer>

      </div>{/* ── end 1595px column ── */}

      {showDoc && <DocModal onClose={() => setShowDoc(false)} />}
    </div>
  );
}